/* =================================================================
   SD MULTI-BUY BUNDLE  —  buy-box bundle selector
   -----------------------------------------------------------------
   Owns ONLY the multi-buy card's own state — it never talks to the
   cart itself:

     • The bundle is NOT the browser cart. Quick add / stepper changes
       never POST to /cart; the single Add-to-Cart posts every line.
     • On every change it publishes the bundle to the page:
         - CustomEvent 'multibuy:change' on document (bubbles to window)
             detail.items = [{ variantId, quantity, price, rangeKey,
                               current, eligible }]
             (price in pence; `current` flags the current product;
              `eligible` says this item's OWN range qualified)
             detail also carries count, subtotal, saving, ranges,
             qualifyingRanges, qualifies
         - window.multibuyState (same data, for late-loading consumers)
     • The per-range saving IS calculated here, as a PREVIEW of the
       offer Shopify applies for real. evaluateRanges() below is the only
       place that rule lives; window.sdMultibuyEvaluate exposes it so a
       consumer can re-run it without copying the arithmetic.

   ── The rule: a saving belongs to a RANGE, never to the basket ──
     1. Items are grouped by rangeKey (data-mb-range-key on the card).
     2. Units are counted per range, separately.
     3. A range qualifies on its OWN count reaching the minimum.
     4. A range holding a single unit is not discounted.
     5. Ranges are NEVER pooled to reach the minimum between them.

   So 1 Foster + 2 Leo discounts the two Leos and nothing else, and
   1 Foster + 1 Leo discounts nothing at all.

   ROUNDING — the percentage is applied to each qualifying LINE and truncated
   there, then summed. That is not an arbitrary choice: it is exactly what
   Shopify's native product discount does when it allocates, so the button and
   the checkout quote the same number to the penny. Flooring the range total
   instead reads as more "correct" but disagrees:
       per line   floor(39999/10) + floor(37999/10) + floor(29999/10) = 10797
       per range  floor(107997/10)                                    = 10799
   The second is the 2p the button used to over-promise on a three-line
   Veloura bundle. Truncation also means the saving can never exceed the
   percentage offered.

   ── Who consumes this ───────────────────────────────────────────
   assets/sd-product.js (SdProduct.prototype.initAddonPriceTotal) listens for
   'multibuy:change' and reads window.multibuyState, so the ATC label shows:
       Sigma(bundle price x qty)  -  saving  +  checked add-ons
   It reads `saving` and never recomputes it — see bundleFigures() there.
   The bundle already carries the current product, so it REPLACES the button's
   data-base-price rather than adding to it.

   SdProduct.prototype.doAddToCart posts the bundle as extra cart lines. The
   item flagged `current: true` is the line the product form already posts, so
   it only contributes its quantity and never becomes a second line.

   ── This is a PREVIEW, not the charge ───────────────────────────
   Nothing here writes to the cart and nothing here discounts an order. What
   actually takes the money off is the Shopify Function / automatic discount
   that lives OUTSIDE this repo (the sd-multibuy-discount app). This file must
   state the SAME rule that Function does, per range and never pooled, or the
   button will quote a figure checkout does not honour. If the Function's
   percentage or minimum changes, change the two theme settings to match
   (Theme settings -> Cart -> Multi-buy saving).
   ================================================================= */
(function () {
  'use strict';

  var EVENT_NAME = 'multibuy:change';

  function money(pence) {
    var fmt =
      (window.Shopify && window.Shopify.money_format) ||
      (window.theme && window.theme.moneyFormat) ||
      '£{{amount}}';
    var amount = (pence / 100).toFixed(2);
    return fmt
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(pence / 100))
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  /* ── evaluateRanges(items, percentage, minimumQuantity) ────────
     THE rule. Pure: it reads the array it is given, mutates nothing, and
     returns fresh objects — so the same call can price the buy-box bundle,
     a cart, or a test fixture.

       items            [{ rangeKey, quantity, price }]  price in pence
       percentage       10 for 10% off
       minimumQuantity  units ONE range must reach to qualify (2)

     Returns
       { items, ranges, count, subtotal, saving, qualifyingRanges, qualifies,
         percentage, minimumQuantity }

     `items` comes back in the order it went in, each entry a shallow copy
     carrying an added `eligible` flag — true only when the item's OWN range
     qualified. `ranges` carries one entry per range key.

     Ranges are held apart by rangeKey and are never summed together: a range
     is tested on its own count, and priced off its own subtotal. Two ranges of
     one unit each therefore save nothing, however many ranges there are.

     Each qualifying range is floored SEPARATELY (step e of the rule). Flooring
     the bundle instead would hand back a different penny on a split basket,
     and the Function bills per range.

     A percentage of 0 or a minimum below 1 means the offer is off rather than
     "everything qualifies" — a missing setting must never discount a cart. */
  function evaluateRanges(items, percentage, minimumQuantity) {
    var list = Array.isArray(items) ? items : [];
    var pct = parseFloat(percentage);
    if (isNaN(pct) || pct <= 0) pct = 0;
    var min = toInt(minimumQuantity);

    /* Group. `order` keeps the first-seen order so the output is stable. */
    var byKey = {};
    var order = [];
    list.forEach(function (item) {
      /* A blank key is its own bucket, not a wildcard: an item with no range
         can only ever qualify alongside other item with no range. */
      var key = String((item && item.rangeKey) || '');
      if (!byKey[key]) {
        byKey[key] = { key: key, count: 0, subtotal: 0, lines: [], qualifies: false, saving: 0 };
        order.push(key);
      }
      var range = byKey[key];
      var quantity = Math.max(0, toInt(item && item.quantity));
      var lineSubtotal = Math.max(0, toInt(item && item.price)) * quantity;
      range.count += quantity;
      range.subtotal += lineSubtotal;
      /* Kept per line, because the saving is truncated per line (see header). */
      range.lines.push(lineSubtotal);
    });

    /* Qualify + price each range on its own. */
    var saving = 0;
    var qualifyingRanges = 0;
    var count = 0;
    var subtotal = 0;
    var ranges = order.map(function (key) {
      var range = byKey[key];
      range.qualifies = pct > 0 && min > 0 && range.count >= min;
      range.saving = 0;
      if (range.qualifies) {
        for (var i = 0; i < range.lines.length; i++) {
          range.saving += Math.floor((range.lines[i] * pct) / 100);
        }
      }
      if (range.qualifies) qualifyingRanges += 1;
      saving += range.saving;
      count += range.count;
      subtotal += range.subtotal;
      return range;
    });

    /* Per-item eligibility, in the caller's original order. */
    var outItems = list.map(function (item) {
      var copy = {};
      for (var k in item) { if (Object.prototype.hasOwnProperty.call(item, k)) copy[k] = item[k]; }
      var range = byKey[String((item && item.rangeKey) || '')];
      copy.eligible = !!(range && range.qualifies);
      return copy;
    });

    return {
      items: outItems,
      ranges: ranges,
      count: count,
      subtotal: subtotal,
      saving: saving,
      qualifyingRanges: qualifyingRanges,
      qualifies: qualifyingRanges > 0,
      percentage: pct,
      minimumQuantity: min
    };
  }

  /* One implementation, reachable from anywhere — the cart, a console, a
     test. Nothing should re-derive this rule locally. */
  window.sdMultibuyEvaluate = evaluateRanges;

  function SdMultibuy(root) {
    this.root = root;
    this.track = root.querySelector('[data-mb-track]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-mb-card]'));
    this.prev = root.querySelector('[data-mb-prev]');
    this.next = root.querySelector('[data-mb-next]');
    this.maxQty = toInt(root.dataset.mbMaxQty) || 10;
    /* The offer, written onto the root by snippets/sd-multibuy-card.liquid
       from Theme settings. Absent = no preview, never a free discount. */
    this.discountPercent = parseFloat(root.dataset.mbDiscount) || 0;
    this.minItems = toInt(root.dataset.mbMinItems);
  }

  /* ── Read the bundle straight off the DOM ─────────────────────
     The DOM is the single source of truth, so the published state can
     never drift from what the shopper sees. */
  SdMultibuy.prototype.readEntries = function () {
    var entries = [];
    this.cards.forEach(function (card) {
      if (card.dataset.mbIncluded !== 'true') return;
      var variantId = toInt(card.dataset.mbVariantId);
      if (!variantId) return;
      var qtyEl = card.querySelector('[data-mb-qty]');
      var quantity = Math.max(1, toInt(qtyEl && qtyEl.value) || 1);
      entries.push({
        card: card,
        item: {
          variantId: variantId,
          quantity: quantity,
          price: toInt(card.dataset.mbPrice),
          /* Which range this item belongs to — what evaluateRanges groups
             on, and the reason two ranges can never top each other up. */
          rangeKey: card.dataset.mbRangeKey || '',
          /* Marks the line the product form already posts, so the ATC never
             adds the current product a second time. */
          current: card.dataset.mbCurrent === 'true'
        }
      });
    });
    return entries;
  };

  SdMultibuy.prototype.readItems = function () {
    return this.readEntries().map(function (entry) { return entry.item; });
  };

  /* ── Paint the card from the evaluation ──────────────────────
     `result.items` comes back in the order it went in, so entry i is the
     card that produced item i. Only a card whose OWN range qualified is
     marked — on a card that ever mixed ranges, one range lighting up must
     not light up the other. */
  SdMultibuy.prototype.renderOffer = function (entries, result) {
    entries.forEach(function (entry, i) {
      var eligible = !!(result.items[i] && result.items[i].eligible);
      entry.card.classList.toggle('is-eligible', eligible);
      entry.card.dataset.mbEligible = eligible ? 'true' : 'false';
      /* The pill follows ELIGIBILITY, not membership: a sofa sitting alone in
         its range is in the bundle and will be added to the cart, but it earns
         nothing, and a badge next to it would promise a saving that neither
         this page nor checkout is going to give. */
      var pill = entry.card.querySelector('[data-mb-pill]');
      if (pill) pill.hidden = !eligible;
    });
    /* Cards outside the bundle carry no verdict at all. */
    this.cards.forEach(function (card) {
      if (card.dataset.mbIncluded === 'true') return;
      card.classList.remove('is-eligible');
      card.dataset.mbEligible = 'false';
      var pill = card.querySelector('[data-mb-pill]');
      if (pill) pill.hidden = true;
    });

    /* The badge is the offer's headline, so it only reads as live once
       something has actually qualified. */
    var badge = this.root.querySelector('[data-mb-badge]');
    if (badge) badge.classList.toggle('is-active', result.qualifies);

    /* The saving line states the figure the button is about to subtract. It
       says nothing at all until a range qualifies — a zero saving spelled
       out is just noise next to the subline that already explains the deal. */
    var savingEl = this.root.querySelector('[data-mb-saving]');
    if (savingEl) {
      if (result.saving > 0) {
        savingEl.textContent = "You'll save " + money(result.saving);
        savingEl.hidden = false;
      } else {
        savingEl.textContent = '';
        savingEl.hidden = true;
      }
    }
  };

  /* ── Publish: window.multibuyState + 'multibuy:change' ──────── */
  SdMultibuy.prototype.publish = function () {
    var entries = this.readEntries();
    var result = evaluateRanges(
      entries.map(function (entry) { return entry.item; }),
      this.discountPercent,
      this.minItems
    );

    this.renderOffer(entries, result);

    /* `items` is the contract — each one now carrying `eligible`, so a
       consumer can price a single line without regrouping. `saving` is the
       sum of the per-range savings and is the ONLY figure downstream should
       subtract: nothing else re-runs the percentage. */
    var detail = {
      items: result.items,
      count: result.count,
      subtotal: result.subtotal,
      saving: result.saving,
      ranges: result.ranges,
      qualifyingRanges: result.qualifyingRanges,
      qualifies: result.qualifies,
      discountPercent: result.percentage,
      minItemsForDiscount: result.minimumQuantity
    };

    window.multibuyState = detail;
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: detail, bubbles: true }));
  };

  /* ── Include / exclude / quantity ────────────────────────────── */
  SdMultibuy.prototype.setIncluded = function (card, included) {
    card.dataset.mbIncluded = included ? 'true' : 'false';
    card.classList.toggle('is-included', included);

    /* The "Included" pill is NOT set here. It now states that the item is
       being DISCOUNTED, not merely that it is in the bundle, so renderOffer()
       owns it — publish() runs at the end of this function and paints it. */

    var qtyEl = card.querySelector('[data-mb-qty]');
    if (qtyEl && included && toInt(qtyEl.value) < 1) qtyEl.value = 1;

    this.syncStepper(card);
    this.publish();
  };

  SdMultibuy.prototype.setQty = function (card, qty) {
    var qtyEl = card.querySelector('[data-mb-qty]');
    if (!qtyEl) return;

    /* Stepping an optional item down to 0 removes it from the bundle and
       gives its Quick add button back — the change stays reversible. The
       current product is the item being bought, so it floors at 1. */
    if (qty < 1) {
      if (card.dataset.mbCurrent === 'true') {
        qtyEl.value = 1;
        this.syncStepper(card);
        this.publish();
      } else {
        qtyEl.value = 1;
        this.setIncluded(card, false);
      }
      return;
    }

    qtyEl.value = Math.min(qty, this.maxQty);
    this.syncStepper(card);
    this.publish();
  };

  SdMultibuy.prototype.syncStepper = function (card) {
    var qtyEl = card.querySelector('[data-mb-qty]');
    var inc = card.querySelector('[data-mb-inc]');
    if (!qtyEl) return;
    var qty = toInt(qtyEl.value);
    if (inc) inc.disabled = qty >= this.maxQty;
    /* Decrement stays enabled: on an optional item it removes the item. */
  };

  /* ── Current product price / variant kept in sync ────────────── */
  SdMultibuy.prototype.updateCurrent = function (variantId, price) {
    var card = this.root.querySelector('[data-mb-card][data-mb-current="true"]');
    if (!card) return;
    if (variantId) card.dataset.mbVariantId = String(variantId);
    if (price || price === 0) {
      card.dataset.mbPrice = String(price);
      var priceEl = card.querySelector('[data-mb-price-el]');
      if (priceEl) priceEl.textContent = money(price);
    }
    this.publish();
  };

  /* ── Carousel ────────────────────────────────────────────────── */
  SdMultibuy.prototype.scrollByCard = function (dir) {
    if (!this.track) return;
    var card = this.cards[0];
    var styles = window.getComputedStyle(this.track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    var step = card ? card.offsetWidth + gap : this.track.clientWidth * 0.5;
    this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  /* Park the arrows on the vertical centre of the image row, whatever the
     column width happens to be. */
  SdMultibuy.prototype.syncNavPosition = function () {
    var media = this.root.querySelector('.sd-mb__media');
    var viewport = this.root.querySelector('.sd-mb__viewport');
    if (!media || !viewport) return;
    var top = media.getBoundingClientRect();
    var base = viewport.getBoundingClientRect();
    this.root.style.setProperty('--mb-nav-top', (top.top - base.top + top.height / 2) + 'px');
  };

  SdMultibuy.prototype.syncNav = function () {
    if (!this.track) return;
    var max = this.track.scrollWidth - this.track.clientWidth;
    var x = this.track.scrollLeft;
    if (this.prev) this.prev.disabled = x <= 1;
    if (this.next) this.next.disabled = x >= max - 1;
  };

  SdMultibuy.prototype.init = function () {
    var self = this;

    if (this.prev) this.prev.addEventListener('click', function () { self.scrollByCard(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.scrollByCard(1); });
    if (this.track) {
      this.track.addEventListener('scroll', function () { self.syncNav(); }, { passive: true });
      window.addEventListener('resize', function () {
        self.syncNav();
        self.syncNavPosition();
      });
    }
    this.syncNav();
    this.syncNavPosition();
    /* Images settle after load and change the media height. */
    window.addEventListener('load', function () { self.syncNavPosition(); });

    /* One delegated click handler for every card control. */
    this.root.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var target = e.target.closest('[data-mb-add], [data-mb-inc], [data-mb-dec]');
      if (!target) return;
      var card = target.closest('[data-mb-card]');
      if (!card) return;
      e.preventDefault();

      if (target.hasAttribute('data-mb-add')) {
        self.setIncluded(card, true);
        return;
      }
      var qtyEl = card.querySelector('[data-mb-qty]');
      var qty = toInt(qtyEl && qtyEl.value) || 1;
      self.setQty(card, target.hasAttribute('data-mb-inc') ? qty + 1 : qty - 1);
    });

    /* Enter in a quantity box must commit the number, never submit a form —
       these blocks render outside <form>, but they are drag-reorderable. */
    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var qtyEl = e.target.closest ? e.target.closest('[data-mb-qty]') : null;
      if (!qtyEl) return;
      e.preventDefault();
      var card = qtyEl.closest('[data-mb-card]');
      if (card) self.setQty(card, toInt(qtyEl.value));
    });

    /* Typed quantities. */
    this.root.addEventListener('change', function (e) {
      var qtyEl = e.target.closest ? e.target.closest('[data-mb-qty]') : null;
      if (!qtyEl) return;
      var card = qtyEl.closest('[data-mb-card]');
      if (card) self.setQty(card, toInt(qtyEl.value));
    });

    this.cards.forEach(function (card) { self.syncStepper(card); });

    /* Keep the current product's card honest if the variant changes. */
    document.addEventListener('sd:variant-changed', function (e) {
      if (!e.detail) return;
      self.updateCurrent(e.detail.variantId || e.detail.id, e.detail.price);
    });

    /* Publish the load-time bundle (current product is auto-included). */
    this.publish();
  };

  function initAll() {
    document.querySelectorAll('[data-mb-root]').forEach(function (root) {
      if (root.dataset.mbInit) return;
      root.dataset.mbInit = '1';
      var instance = new SdMultibuy(root);
      instance.init();
      root.sdMultibuy = instance;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  /* Theme editor re-renders the section without a page load. */
  document.addEventListener('shopify:section:load', initAll);
})();
